import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgElement, WithProperties } from '@angular/elements';
import DataSource from 'devextreme/data/data_source';
import { UserDomainService } from '../user-domains/user-domain.service';
import { Field, Tracker, Tractor, TrailerIntervention, User, Notification, Vehicle } from 'shared';
import { interval, Subject, Subscription } from 'rxjs';
import { WebsocketService } from '../services/websocket/websocket.service';
import * as L from 'leaflet';
import { GeoJSON, Marker, MarkerClusterGroup, Map as LMap, Control } from 'leaflet';
import 'leaflet.markercluster';
import { MapService } from '../services/map.service';
import { debounceTime, filter, first } from 'rxjs/operators';
import { zip } from 'rxjs';
import '../../../../../node_modules/leaflet-rotatedmarker/leaflet.rotatedMarker.js';
import { MapTractorPopupComponent } from './map-tractor-popup/map-tractor-popup.component';
import { TrailerInterventionStatus } from 'shared';
import { FieldService } from '../stores/field.service';
import { DxDataGridComponent, DxPopoverComponent, DxTooltipComponent, DxTextBoxComponent } from 'devextreme-angular';
import { FollowInterventionWSE, StopFollowInterventionWSE } from '../services/websocket/websocket-event';
import { MapInterventionPopupComponent } from '../map/map-intervention-popup/map-intervention-popup.component';
import { LineString } from 'geojson';
import { LayoutService } from '../home/layout.service';
import { NotificationService } from '../stores/notification.service';
import { TRAILER_INTERVENTION_OVER_SPEED_NOTIFICATION } from '../../../../shared/src/lib/models/notification.model';
import { GlOnResize, GoldenLayoutContainer } from 'ngx-golden-layout';
import GoldenLayout from 'golden-layout';
import { DomSanitizer } from '@angular/platform-browser';
import { MapSidebarFilterService } from '../map/map-sidebar-filter/map-sidebar-filter.service';
import { TractorByImeiService } from '../stores/tractor-by-imei.service';
import CustomStore from 'devextreme/data/custom_store';
import { VehiclesIconSelectorComponent } from '../vehicles/vehicles-icon-selector/vehicles-icon-selector.component';
import moment from 'moment';

@Component({
  selector: 'app-map-tractors',
  templateUrl: './map-tractors.component.html',
  styleUrls: ['./map-tractors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapTractorsComponent implements OnInit, OnDestroy, AfterViewInit, GlOnResize {

  remoteDs: DataSource;
  remoteStore: CustomStore;
  // list: Tractor[];
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;

  // Map data
  map: LMap;
  mapClusterGroup: MarkerClusterGroup;
  markers: Map<number, Marker> = new Map<number, Marker>();
  private tractorsByImei: Map<number, Tractor> = new Map<number, Tractor>();
  private trackerConnectionState: Map<number, boolean> = new Map<number, boolean>();
  private markerPopups: Map<number, NgElement & WithProperties<MapTractorPopupComponent>> =
    new Map<number, NgElement & WithProperties<MapTractorPopupComponent>>();

  // Interventions / Popover / Control data
  @ViewChild(DxPopoverComponent, { static: true })
  popover: DxPopoverComponent;
  interventionTractor: Tractor;
  interventionField?: Field;

  // Interventions / Tooltip / Control data
  @ViewChild(DxTooltipComponent, { static: true })
  tooltip: DxTooltipComponent;
  tooltipData: any;

  // Follow Intervention
  followInter?: TrailerIntervention;
  followInterMultiPolygon?: GeoJSON;
  followInterControl: Control;
  @ViewChild(MapInterventionPopupComponent, { static: true, read: ElementRef })
  followInterControlEl: ElementRef;
  // Follow tracker
  followTrackerLineString: GeoJSON;
  followTrackerLineStringPoints: number[][] = [];
  followTrackerImei = 0;
  // Notifications
  notificationsSub: Subscription;
  // Fuel Realtime
  fuelRealtimeConsumptionPopups = {};

  defaultVisible = true;

  @ViewChild(VehiclesIconSelectorComponent)
  iconsSelector: VehiclesIconSelectorComponent;

  @ViewChild(DxDataGridComponent)
  grid: DxDataGridComponent;

  @ViewChild(DxTextBoxComponent)
  searchTxtBox: DxTextBoxComponent;
  private sideFilterSub: Subscription;
  private autoRefreshSub: Subscription;
  private autoRefreshInProgress = false;
  private liveRefreshDs: DataSource;
  private liveRefreshSyncTimer: any;

  groupExpanded = true;
  groupToggleIcon: string = this.groupExpanded ? 'collapse' : 'expand';

  baseFilters = ['tracker_id', '<>', null];

  dsLoadChanged$ = new Subject<void>();

  constructor(
    private tractorService: TractorByImeiService,
    private fieldService: FieldService,
    private userDomainsService: UserDomainService,
    public user: User,
    private notificationService: NotificationService,
    private ws: WebsocketService,
    private layoutService: LayoutService,
    private mapService: MapService,
    public domSanitizer: DomSanitizer,
    private mapSidebarFilter: MapSidebarFilterService,
    @Inject(GoldenLayoutContainer) private container: GoldenLayout.Container,
  ) {

    // this.listHeight = container.height - 7;

    // Bindings
    this.startFollowIntervention = this.startFollowIntervention.bind(this);

    // Create a local store from remote one
    // this.createLocalStore();

    // Permissions
    this.canEdit = user.can('edit tractors');
    this.canAdd = user.can('add tractors');
    this.canDelete = user.can('delete tractors');

    // Create map group
    this.mapClusterGroup = L.markerClusterGroup({
      maxClusterRadius: 20,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      disableClusteringAtZoom: 16,
    });

    // Wait map ready
    this.mapService.mapReady$.pipe(first()).subscribe((map) => {
      this.map = map;
      this.mapClusterGroup.addTo(map);
    });

    // Notifications
    this.notificationsSub = this.notificationService.navigation
      .pipe(
        filter(notification => notification.type === TRAILER_INTERVENTION_OVER_SPEED_NOTIFICATION),
      ).subscribe((notification) => {
        this.navigateToNotification(notification);
      });
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    if (this.notificationsSub) {
      this.notificationsSub.unsubscribe();
    }
    if (this.sideFilterSub) {
      this.sideFilterSub.unsubscribe();
    }
    if (this.autoRefreshSub) {
      this.autoRefreshSub.unsubscribe();
    }
    if (this.liveRefreshSyncTimer) {
      clearTimeout(this.liveRefreshSyncTimer);
    }
  }

  ngAfterViewInit() {
    const el = this.followInterControlEl.nativeElement as HTMLElement;
    const custom = Control.extend({
      onAdd(m: LMap) {
        // el.classList.remove('hidden');
        return el;
      },
      onRemove(m: LMap) {
        // el.classList.add('hidden');
      }
    });
    this.followInterControl = new custom({
      position: 'bottomleft'
    });

    // Init dataSource
    this.remoteDs = this.tractorService.newDataSource({
      paginate: false,
      pageSize: 100000,
      reshapeOnPush: true,
      pushAggregationTimeout: 500,
      filter: this.baseFilters,
      onLoadingChanged: (isLoading) => {
        if (isLoading) {
          return;
        }
        this.dsLoadChanged$.next();
      }
    });

    // Remote store
    this.remoteStore = this.remoteDs.store();

    // A separate datasource keeps polling invisible to the displayed grid.
    this.liveRefreshDs = this.tractorService.newDataSource({
      paginate: false,
      pageSize: 100000,
      filter: this.baseFilters,
    });

    // Defer rendering
    this.grid.instance.option('dataSource', this.remoteDs);

    // Sidebar filter
    this.sideFilterSub = this.mapSidebarFilter.filter$.subscribe(() => {
      // Update map
      zip(
        this.dsLoadChanged$,
        this.mapService.mapReady$,
        this.grid.onOptionChanged.pipe(
          filter(o => o.name === 'columns'),
          debounceTime(200),
        )
      ).pipe(
        first()
      ).subscribe(() => {
        this.updateMap();
      });
      this.mapSidebarFilter.applyOnGrid(this.grid.instance, this.baseFilters);
      this.grid.instance.repaint();
    });

    // Keep status, domain assignments and database-side changes current even
    // when no tracker packet is emitted (for example, online -> offline timeout).
    this.autoRefreshSub = interval(10000).subscribe(() => this.refreshLiveData());

    // Tracking events
    this.ws.topicObserver('Tracker', Tracker).subscribe((e) => {
      // Update the store
      this.remoteStore.push([{ type: 'update', data: { tracker: e }, key: e.imei }]);
      const tractor = this.tractorsByImei.get(e.imei);
      if (tractor) {
        tractor.tracker = e;
        const popup = this.markerPopups.get(e.imei);
        if (popup) {
          popup.tractor = tractor;
        }
      }
      if (this.followTrackerImei !== 0 && this.followTrackerImei === e.imei) {
        this.updateFollow(e.lat, e.lng);
      }
      // Update map
      this.updateMarker(e);
    });

    // Interventions status events
    this.ws.topicObserver('TrailerInterventionStatus', TrailerInterventionStatus).subscribe((e) => {
      // Update the store
      this.remoteStore.push([{ type: 'update', data: { interventionStatus: e }, key: e.tracker_imei }]);
    });

    // Interventions status events
    this.ws.topicObserver('TrailerIntervention', TrailerIntervention).subscribe((e) => {
      this.followInter = e;
      this.updateFollowIntervention();
    });
  }

  addMarker(i: Tractor) {
    this.tractorsByImei.set(i.tracker.imei, i);
    this.trackerConnectionState.set(
      i.tracker.imei,
      this.isTractorConnected(i.tracker.tracker_time),
    );
    const icon = L.icon({
      iconUrl: i.tracker.stateArrowIcon(),
      iconSize: [16, 24],
      iconAnchor: [8, 24]
    });

    const marker = new Marker([i.tracker.lat, i.tracker.lng], {
      icon,
      // @ts-ignore
      rotationAngle: i.tracker.angle,
      rotationOrigin: 'center center',
    });

    marker.bindTooltip(i.name, {
      permanent: true,
      offset: [0, -30],
      direction: 'top',
      opacity: 0.6,
    });

    let popupEl: NgElement & WithProperties<MapTractorPopupComponent> = null;

    // Add to the DOM
    // document.body.appendChild(popupEl);
    // Listen to the close event
    /*popupEl.addEventListener('closed', () => {

      console.log('Colses');
      document.body.removeChild(popupEl);
    });*/

    marker.bindPopup(() => {
      if (popupEl === null) {
        popupEl = document.createElement('tractor-popup') as any;
        this.markerPopups.set(i.tracker.imei, popupEl);
      }
      popupEl.tractor = this.tractorsByImei.get(i.tracker.imei) || i;
      return popupEl;
    }, {
      offset: [0, -20],
      minWidth: 300,
      className: 'tractor-map-popup',
    });

    this.mapClusterGroup.addLayer(marker);

    this.markers.set(i.tracker.imei, marker);
  }

  private updateMapRecursive(list: (Tractor & { items })[]) {

    if (!list) {
      return;
    }

    list.forEach(t => {
      if (t.hasOwnProperty('items')) {
        this.updateMapRecursive(t.items);
      } else {
        this.addMarker(t);
      }
    });
  }

  private updateMap() {
    // Clear layers
    this.mapClusterGroup.clearLayers();

    // Clear dict
    this.markers.clear();
    this.tractorsByImei.clear();
    this.trackerConnectionState.clear();
    this.markerPopups.clear();

    const list = this.remoteDs.items();

    this.updateMapRecursive(list);

    const bounds = this.mapClusterGroup.getBounds();
    if (Object.keys(bounds).length > 0) {
      this.map.fitBounds(bounds, {
        padding: [100, 100],
        maxZoom: 12,
      });
    }
  }

  private updateMarker(t: Tracker) {
    const marker = this.markers.get(t.imei);
    if (!marker) {
      return;
    }

    // Update position
    marker.setLatLng([t.lat, t.lng]);

    // Update icon
    marker.setIcon(L.icon({
      iconUrl: t.stateArrowIcon(),
      iconSize: [16, 24],
      iconAnchor: [8, 24]
    }));

    // Update angle
    // @ts-ignore
    marker.setRotationAngle(t.angle);
  }

  private flattenTractors(list: (Tractor & { items?: any[] })[], result: Tractor[] = []): Tractor[] {
    if (!list) {
      return result;
    }

    list.forEach(item => {
      if (item && item.hasOwnProperty('items')) {
        this.flattenTractors(item.items as any, result);
      } else if (item && item.tracker) {
        result.push(item);
      }
    });
    return result;
  }

  /** Reconcile markers without changing zoom, map center or an open popup. */
  private syncMapData(): void {
    const tractors = this.flattenTractors(this.remoteDs.items() as any);
    const currentImeis = new Set<number>();

    tractors.forEach(tractor => {
      const imei = tractor.tracker.imei;
      currentImeis.add(imei);
      this.tractorsByImei.set(imei, tractor);

      if (this.markers.has(imei)) {
        this.updateMarker(tractor.tracker);
        const marker = this.markers.get(imei);
        if (marker && marker.getTooltip()) {
          marker.setTooltipContent(tractor.name);
        }
      } else {
        this.addMarker(tractor);
      }

      const popup = this.markerPopups.get(imei);
      if (popup) {
        popup.tractor = tractor;
      }
    });

    Array.from(this.markers.keys()).forEach(imei => {
      if (currentImeis.has(imei)) {
        return;
      }
      const marker = this.markers.get(imei);
      if (marker) {
        this.mapClusterGroup.removeLayer(marker);
      }
      this.markers.delete(imei);
      this.tractorsByImei.delete(imei);
      this.trackerConnectionState.delete(imei);
      this.markerPopups.delete(imei);
    });
  }

  contextMenuClick(e: any, tractor: Tractor) {
    switch (e.itemData.action) {
      case 'history':
        this.layoutService.openTab({
          id: 'tractors-history-' + tractor.id,
          type: 'component',
          title: 'Historique',
          componentName: 'tractors-history',
          componentState: { tractor }
        });
        /*const layout = new TractorsHistoryLayout(this.layoutService, item);
        layout.open();*/
        break;
      case 'fuel-realtime-consumption':
        this.fuelRealtimeConsumptionPopups[tractor.tracker.imei] = !this.fuelRealtimeConsumptionPopups[tractor.tracker.imei];
        break;
      case 'follow':
        // this.store.byKey(e.itemData.id).then((item) => {
        // this.tractorService.flyTo(item, this.mapService.map);
        // });
        break;
    }
  }

  itemClick(e: any) {
    const marker = this.markers.get(e.itemData.tracker.imei);
    if (!marker) {
      return;
    }
    this.mapService.mapReady$.pipe(first()).subscribe((map) => {

      map.fitBounds(marker.getLatLng().toBounds(5), {
        padding: [50, 50],
        maxZoom: 17,
      });

      marker.openPopup();
    });
  }

  startFollowIntervention(e: any) {
    const imei = this.interventionTractor.tracker.imei;

    // Stop previous follow of intervention/tracker
    if (this.followInter) {
      this.stopFollowIntervention();
    }
    if (this.followTrackerImei) {
      this.stopFollow();
    }

    // Start new follow
    this.ws.send(new FollowInterventionWSE(imei));
    this.followInterControl.addTo(this.map);
    this.popover.instance.hide();

    const marker = this.markers.get(this.interventionTractor.tracker.imei);

    this.map.fitBounds(marker.getLatLng().toBounds(5), {
      padding: [50, 50],
      maxZoom: 17,
    });

    // Start follow the tracker too
    this.startFollow(imei);
  }

  isTractorConnected(tracker_timer: string) {
    var dateDifference = Date.now() - Date.parse(tracker_timer);

    if (Math.abs(dateDifference) > 300000) {
      return false;
    } else {
      return true;
    }
  }

  updateFollowIntervention() {
    if (this.followInterMultiPolygon) {
      this.followInterMultiPolygon.remove();
    }
    this.followInterMultiPolygon = this.followInter.toGeoJSON();
    if (!this.followInterMultiPolygon) {
      return;
    }
    this.followInterMultiPolygon.setStyle({
      fillColor: '#4ef34a',
      fill: true,
      weight: 1,
      interactive: true,
      fillOpacity: 0.6,
      color: '#04b800',
      stroke: true,
    });
    this.followInterMultiPolygon.addTo(this.map);
  }

  stopFollowIntervention() {
    this.ws.send(new StopFollowInterventionWSE());
    // Started the intervention but no update to create the polygon
    if (this.followInterMultiPolygon) {
      this.followInterMultiPolygon.remove();
      this.followInterMultiPolygon = null;
    }
    this.followInterControl.remove();
    this.followInter = null;
    this.stopFollow();
  }

  /**
   * Show the intervention status popover
   * @param e mouse event
   * @param tractor the tractor
   */
  showInterventionPopover(e: MouseEvent, tractor: Tractor) {
    if (!tractor.interventionStatus) {
      return;
    }

    // Assign the tractor
    this.interventionTractor = tractor;

    // Try to find the field if available
    if (!tractor.interventionStatus.field_id) {
      this.interventionField = null;
    } else {
      this.interventionField = this.fieldService.dataSource
        .items()
        .find(f => f.id === tractor.interventionStatus.field_id);
    }

    // Show the popover
    this.popover.instance.show('#tractor-badge-' + tractor.id);
  }

  /**
   * Show the tracker status tooltip
   * @param e mouse event
   * @param data the cell data
   */
  showTrackerStateTooltip(e: MouseEvent, data: any) {

    this.tooltipData = "Dernière mise à jour : <b>"+moment(data.tracker.tracker_time).format('YYYY-MM-DD HH:mm:ss')+"</b>";

    // Show the popover
    this.tooltip.instance.show('#tracker-state-' + data.id);
  }

  showTrackerCardTooltip(e: MouseEvent, data: any) {
    const params = data.tracker.params || {};

    if (params.BCEBeaconID || params.BLEBeaconID) {
      const isBCE = !!params.BCEBeaconID;
      const identifier = isBCE ? params.BCEBeaconID : params.BLEBeaconID;
      const mac = isBCE ? params.BCEBeaconMAC : params.BLEBeaconMAC;
      this.tooltipData = 'Type : <b>' + (isBCE ? 'Balise BCE' : 'Balise Bluetooth') + '</b><br>' +
        'Identifiant : <b>' + identifier + '</b><br>' +
        'MAC : <b>' + mac + '</b>';
    } else if (params.RFID) {
      this.tooltipData = 'Carte RFID : <b>' + params.RFID + '</b>';
    } else {
      this.tooltipData = 'Aucune balise ou carte détectée';
    }

    // Show the popover
    this.tooltip.instance.show('#tracker-card-' + data.id);
  }

  showTrackerSwitchTooltip(e: MouseEvent, data: any){
    if(data.tracker.params.IN2 == 0){
      this.tooltipData = 'Switch : Off';
    } else {
      this.tooltipData = 'Switch : On';
    }


    // Show the popover
    this.tooltip.instance.show('#tracker-switch-' + data.id);
  }

  private startFollow(imei: number) {
    // Remove previous linestring
    if (this.followTrackerLineString) {
      this.followTrackerLineString.remove();
    }

    // Set the imei
    this.followTrackerImei = imei;

    // Reset points
    this.followTrackerLineStringPoints = [];
  }

  private updateFollow(lat: number, lng: number) {
    // Remove previous linestring
    if (this.followTrackerLineString) {
      this.followTrackerLineString.remove();
    }

    // Add new point
    this.followTrackerLineStringPoints.push([lng, lat]);

    // Construct linestring
    const ls: LineString = {
      type: 'LineString',
      coordinates: this.followTrackerLineStringPoints,
    };

    this.followTrackerLineString = new GeoJSON<LineString>(ls);

    this.followTrackerLineString.setStyle({
      color: '#f30000',
      weight: 2,
      opacity: 0.75
    });

    // Add the new linestring
    this.followTrackerLineString.addTo(this.map);
  }

  private stopFollow() {
    // Remove previous linestring
    if (this.followTrackerLineString) {
      this.followTrackerLineString.remove();
    }

    // Set the imei
    this.followTrackerImei = 0;

    // Reset points
    this.followTrackerLineStringPoints = [];
    this.followTrackerLineString = null;
  }

  private navigateToNotification(notification: Notification) {
    // console.log(notification);
  }

  glOnResize(): void {
    // this.listHeight = this.container.height;
  }

  changeIcon(e: MouseEvent, vehicle: Tractor) {
    e.stopPropagation();
    this.iconsSelector.changeIcon(vehicle as Vehicle);
  }

  searchGrid(e: any) {
    // Update map
    zip(
      this.dsLoadChanged$,
      this.mapService.mapReady$,
      this.grid.onOptionChanged.pipe(
        filter(o => o.name === 'searchPanel'),
      )
    ).pipe(
      first()
    ).subscribe(() => {
      this.updateMap();
    });
    this.grid.instance.searchByText(e.value);
  }

  onToolbarPreparing(e: any) {
    const toolbarItems = e.toolbarOptions.items;

    toolbarItems.forEach((item) => {
      if (item.name === 'searchPanel') {
        item.location = 'before';
      }
    });
  }

  onGridContentReady(e: any) {
    e.element.querySelector('colgroup')?.remove();
    e.element.querySelectorAll('tbody > tr').forEach((node) => {
      const td = node.getElementsByTagName('td');
      while (td.length > 1) {
        td[0].remove();
      }
      td[0].removeAttribute('colspan');
    });
  }

  onRowClick(e: any) {
    if (e.rowType === 'group') {
      return;
    }
    this.visit(e.data.tracker.imei);
  }

  visit(imei: number) {
    const marker = this.markers.get(imei);
    if (!marker) {
      return;
    }
    this.mapService.mapReady$.pipe(first()).subscribe((map) => {

      map.fitBounds(marker.getLatLng().toBounds(5), {
        padding: [50, 50],
        maxZoom: 17,
      });

      marker.openPopup();
    });
  }


  filterClick = () => {
    this.mapSidebarFilter.show();
  }

  toggleSearch = (e: any) => {
    this.searchTxtBox.instance.focus();
  }

  private refreshLiveData(): void {
    if (this.autoRefreshInProgress || !this.liveRefreshDs || !this.remoteStore) {
      return;
    }

    this.autoRefreshInProgress = true;
    this.liveRefreshDs.load()
      .then(() => {
        const latest = this.flattenTractors(this.liveRefreshDs.items() as any);
        const visible = this.flattenTractors(this.remoteDs.items() as any);
        const latestImeis = new Set(latest.map(tractor => tractor.tracker.imei));
        const visibleByImei = new Map(
          visible.map(tractor => [tractor.tracker.imei, tractor] as [number, Tractor]),
        );
        const changes: any[] = [];
        let structureChanged = false;

        latest.forEach(tractor => {
          const imei = tractor.tracker.imei;
          const current = visibleByImei.get(imei);
          const connected = this.isTractorConnected(tractor.tracker.tracker_time);
          const connectionChanged = this.trackerConnectionState.get(imei) !== connected;
          const dataChanged = !current || JSON.stringify(current) !== JSON.stringify(tractor);

          this.trackerConnectionState.set(imei, connected);
          if (!dataChanged && !connectionChanged) {
            return;
          }

          changes.push({
            type: current ? 'update' : 'insert',
            key: imei,
            data: tractor,
          });
          structureChanged = structureChanged || !current;

          // Refresh an existing marker and open popup immediately.
          if (this.markers.has(imei)) {
            this.tractorsByImei.set(imei, tractor);
            this.updateMarker(tractor.tracker);
            const popup = this.markerPopups.get(imei);
            if (popup) {
              popup.tractor = tractor;
            }
          }
        });
        visible.forEach(tractor => {
          if (!latestImeis.has(tractor.tracker.imei)) {
            changes.push({
              type: 'remove',
              key: tractor.tracker.imei,
            });
            structureChanged = true;
          }
        });

        if (changes.length) {
          this.remoteStore.push(changes);
        }

        // reshapeOnPush is delayed by 500 ms; reconcile only additions/removals.
        if (structureChanged) {
          if (this.liveRefreshSyncTimer) {
            clearTimeout(this.liveRefreshSyncTimer);
          }
          this.liveRefreshSyncTimer = setTimeout(() => this.syncMapData(), 600);
        }
      })
      .catch(() => undefined)
      .then(() => {
        this.autoRefreshInProgress = false;
      });
  }

  refreshGrid = (e: any) => {
    this.refreshLiveData();
  }

  groupsToggle = (e: any) => {
    if (this.groupExpanded) {
      this.grid.instance.collapseAll();
      this.groupExpanded = false;
      this.groupToggleIcon = 'expand';
    } else {
      this.grid.instance.expandAll();
      this.groupExpanded = true;
      this.groupToggleIcon = 'collapse';
    }
    this.dsLoadChanged$.pipe(first()).subscribe(() => this.updateMap());
  }

  onRowExpanded(e: any) {
    this.updateMap();
  }

  onRowCollapsed(e: any) {
    this.updateMap();
  }
}

